<template>
  <div id="configurator" class="mb-7 space-y-32">
    <!--
      [CondensedFunnel] The sticky bar portal pieces are rendered directly by
      this component — the condensed funnel deliberately has no wrapper
      component (unlike the classic CustomizationFunnel + Wrapper pair).
    -->
    <RevPortal defer :to="`#${STICKY_BARS_PORTAL_TARGET}`">
      <StickyBarTop
        :price
        :price-without-subsidies
        :product
        :selected-mobile-plan
        :selected-offer
        :selected-options
        :telco-selection="telcoCartSelection"
        :tracking="productTracking"
        :upgrade-picker-fields="upgradeFields"
      >
        <template #below-bar>
          <DealBanner
            v-if="selectedOffer.price && deal"
            class="absolute top-full inset-x-0"
            :deal
            :price="selectedOffer.price"
          />
          <FreeShippingBanner
            v-else-if="shouldDisplayFreeShippingBanner"
            class="absolute top-full inset-x-0"
          />
        </template>
        <!--
          [ToDo CondensedFunnel] the progress bar will be recreated for the
          condensed design; it won't rely on the classic currentStep tracking.
        -->
      </StickyBarTop>

      <StickyBarTopSmall
        :select="selectedOffer.shipping"
        :selected-options
        :shipping="selectedOffer.shipping"
        :upgrade-picker-fields="upgradeFields"
      >
        <!-- [ToDo CondensedFunnel] progress bar slot left empty for now -->
      </StickyBarTopSmall>

      <ClientOnly>
        <UpgradeForLess
          :pickers
          :product
          @upgrade-accepted="handleUpgradeAccepted"
        />
      </ClientOnly>
    </RevPortal>

    <template v-for="step in funnel" :key="step.id">
      <!-- Telco picker: placement is declared in funnel data (step.showTelcoPickerBefore) -->
      <PdpErrorBoundary v-if="step.showTelcoPickerBefore">
        <ErrorOwners :owners="['bot-squad-partnerships-front']">
          <TelcoPlanPicker
            v-if="isPickerEligible"
            :is-picker-eligible
            :options="telcoOptions"
            :selected-partner-name
            @select-plan="telcoSelectPlan"
          />
        </ErrorOwners>
      </PdpErrorBoundary>

      <div>
        <PdpErrorBoundary v-if="step.id === 'trade_in_au'">
          <ErrorOwners :owners="['bot-squad-apac-front']">
            <StepTradeInAU :without-left-template="true" />
          </ErrorOwners>
        </PdpErrorBoundary>

        <!-- StepTradeIn handles its own errors internally via BuybackErrorBoundary -->
        <StepTradeIn
          v-else-if="
            step.id === 'trade_in' && isSwapEnabled && product.isTradeInEligible
          "
          :category="product.tracking.categoryName"
          :is-focused="true"
          :model="product.model"
          :price="priceWithoutSubsidies || undefined"
          :without-left-template="true"
        />

        <StepRow
          v-else-if="step.id !== 'trade_in'"
          :first-picker-id
          :hide-guidance-card="step.id === 'battery' && hideBatteryGuidanceCard"
          :step
        >
          <template v-if="step.id === 'grades'" #guidance-grades>
            <ContainerCondensedFunnelGradesCarousel
              :brand="product.brand"
              :content="step.sideContent"
              :tracking-model="product.model"
            />
          </template>
        </StepRow>
      </div>
    </template>

    <!-- [ToDo CondensedFunnel] PromoCodeStep pending confirmation (PPI-8047) -->

    <Proposal
      v-if="!hasNoVariants && funnel.length > 0"
      :mobile-plan="selectedMobilePlan"
      :price
      :price-without-subsidies
      :product
      :selected-color-code
      :selected-offer
      :selected-options
      :telco-selection="telcoCartSelection"
      :tracking="productTracking"
      :upgrade-picker-fields="upgradeFields"
      :without-left-template="true"
    >
      <template #details>
        <Accessories
          :comes-with="product.comesWith"
          :partner-promo-codes="product.includedServiceOffers.partnerPromoCodes"
        />

        <SellerComment
          v-if="selectedOffer && selectedOffer.sellerComment"
          :comment="selectedOffer.sellerComment"
        />
      </template>
    </Proposal>

    <ContainerAdditionalInfo />

    <ContainerVat v-if="hasNoVariants" class="my-32 md:my-56" />
  </div>

  <div :id="STICKY_BARS_PORTAL_TARGET" />

  <ClientOnly>
    <NewBatteryModal
      v-if="!isNewBatteryRemoved"
      :brand="product.brand"
      :category-id="product.tracking.categoryId"
      :device-name="product.titles.raw"
    />
    <SimModal v-if="showSimModal" />
    <MacbookProcessorModal v-if="showMacbookProcessorModal" />
    <LaptopProcessorModal v-if="showLaptopProcessorModal" />
    <MobilePlanOffersDrawer v-if="showMobilePlanOffersDrawer" :offers />
    <ConditionDescriptionModal
      v-if="gradeStep"
      :brand="product.brand"
      :category-id="product.tracking.categoryId"
      :options="gradeStep.options"
    />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Price } from '@backmarket/http-api/src/api-models/Price'
import {
  type GetLegacyServicePickersResponse,
  type GetPickersResponse,
  hasItemExtraData,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import DealBanner from '@backmarket/nuxt-layer-deals/DealBanner.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorOwners from '@backmarket/nuxt-module-logger/components/ErrorOwners.vue'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevPortal } from '@ds/components/Portal'

import { ClientOnly } from '#components'

import StepTradeIn from '~/scopes/buyback/swap/components/SwapStep/TradeInStep.vue'
import { useSwapEnabled } from '~/scopes/buyback/swap/composables/useSwapEnabled/useSwapEnabled'
import ContainerCondensedFunnelGradesCarousel from '~/scopes/product-core/components/containers/ContainerCondensedFunnelGradesCarousel/ContainerCondensedFunnelGradesCarousel.vue'
import ContainerAdditionalInfo from '~/scopes/product-core/components/containers/ContainerCustomizationFunnel/components/ContainerAdditionalInfo/ContainerAdditionalInfo.vue'
import ContainerVat from '~/scopes/product-core/components/containers/ContainerVat/ContainerVat.vue'
import Accessories from '~/scopes/product-core/components/presentationals/Accessories/Accessories.vue'
import ConditionDescriptionModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ConditionDescriptionModal/ConditionDescriptionModal.vue'
import MobilePlanOffersDrawer from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/MobilePlanOffersDrawer/MobilePlanOffersDrawer.vue'
import NewBatteryModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/NewBatteryModal/NewBatteryModal.vue'
import LaptopProcessorModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/LaptopProcessorModal/LaptopProcessorModal.vue'
import MacbookProcessorModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/MacbookProcessorModal/MacbookProcessorModal.vue'
import Proposal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/Proposal/Proposal.vue'
import SimModal from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/SimModal/SimModal.vue'
import StickyBarTop from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/StickyBarTop/StickyBarTop.vue'
import StickyBarTopSmall from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/StickyBarTopSmall/StickyBarTopSmall.vue'
import UpgradeForLess from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/UpgradeForLess/UpgradeForLess.vue'
import StepRow from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/components/StepRow/StepRow.vue'
import { useCondensedFunnelData } from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/composables/useCondensedFunnelData/useCondensedFunnelData'
import FreeShippingBanner from '~/scopes/product-core/components/presentationals/FreeShippingBanner/FreeShippingBanner.vue'
import PdpErrorBoundary from '~/scopes/product-core/components/presentationals/PdpErrorBoundary/PdpErrorBoundary.vue'
import SellerComment from '~/scopes/product-core/components/presentationals/SellerComment/SellerComment.vue'
import { useBatteryConfidenceExperiment } from '~/scopes/product-core/composables/useBatteryConfidenceExperiment/useBatteryConfidenceExperiment'
import { useProductTracking } from '~/scopes/product-core/composables/useFetch/useFetchProductTracking/composables/useProductTracking/useProductTracking'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import {
  MACBOOKS_CATEGORIES,
  ProductCategory,
  STICKY_BARS_PORTAL_TARGET,
} from '~/scopes/product-core/constants'
import TelcoPlanPicker from '~/scopes/product-core/pages/ProductDetailPage/us/components/presentationals/TelcoPlanPicker/TelcoPlanPicker.vue'
import { useTelcoPicker } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/useTelcoPicker'
import StepTradeInAU from '~/scopes/tradein-apac/components/CustomizationFunnelStep/StepAU.vue'

const props = withDefaults(
  defineProps<{
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    pickers: GetPickersResponse
    pickersServices: GetLegacyServicePickersResponse | undefined
    price: Price
    priceWithoutSubsidies?: Price | null
    priceWithoutTradeIn?: Price | null
    product: GetProductResponse
    productTracking: ReturnType<typeof useProductTracking>
    selectedMobilePlan?: MobilePlanOffer
  }>(),
  {
    selectedMobilePlan: undefined,
    priceWithoutSubsidies: undefined,
    priceWithoutTradeIn: null,
  },
)

const route = useRoute()
const i18n = useI18n()
const experiments = useExperiments()
const { getDiscountedPrice } = useBuybackOffer()
const { mobilePlan } = useUrlParams()
const marketplace = useMarketplace()
const { isSwapEnabled } = useSwapEnabled()
const { isControl: isBatteryConfidenceControl } =
  useBatteryConfidenceExperiment()

const {
  isPickerEligible,
  options: telcoOptions,
  selectedPartnerName,
  telcoCartSelection,
  selectPlan: telcoSelectPlan,
} = useTelcoPicker(() => props.product)

const { steps } = useCondensedFunnelData(() => ({
  pickers: props.pickers,
  pickersServices: props.pickersServices?.pickerGroups ?? [],
  product: props.product,
  price: props.priceWithoutSubsidies,
  discountedPrice: getDiscountedPrice(props.priceWithoutTradeIn),
}))

const isNewBatteryRemoved = computed(
  () =>
    marketplace.market.countryCode === MarketCountryCode.US &&
    experiments['experiment.usNewBattery'] === 'withoutBatteryPicker',
)

/**
 * List of steps (rows) that conform the condensed funnel.
 */
const funnel = computed(() => {
  if (isNewBatteryRemoved.value) {
    return steps.value.filter(({ id }) => id !== 'battery')
  }

  return steps.value
})

const hideBatteryGuidanceCard = computed(
  () =>
    !isBatteryConfidenceControl.value &&
    props.product.tracking.categoryId === ProductCategory.SMARTPHONE,
)

const deal = computed(() => {
  if (experiments['experiment.flashSalesOnPDP'] !== 'enabled') return null

  return props.selectedOffer.deals?.[0] ?? null
})

const shouldDisplayFreeShippingBanner = computed(() => {
  return experiments['experiment.ppFreeShippingBanner'] === 'withBanner'
})

const upgradeFields = ref<
  NonNullable<GetPickersResponse['upgrade']>['pickerFields'] | undefined
>(undefined)

function handleUpgradeAccepted(
  pickerFields?: NonNullable<GetPickersResponse['upgrade']>['pickerFields'],
) {
  upgradeFields.value = pickerFields
}

watch(
  () => props.pickers,
  (_newValue, oldValue) => {
    // !oldValue.upgrade means that the previous selection was the upgraded one
    if (!oldValue.upgrade && upgradeFields.value) {
      upgradeFields.value = undefined
    }
  },
)

const hasNoVariants = computed(() => {
  return props.pickers.pickerGroups.length === 0
})

const firstPickerId = computed(() => {
  return props.pickers.pickerGroups[0]?.id
})

const gradeStep = computed(() => {
  return funnel.value.find(({ id }) => id === 'grades')
})

const selectedOptions = computed(() => {
  const variants = funnel.value
    ?.reduce<Array<string>>((acc, { options, type }) => {
      // We don't want to display in the proposal the service steps
      if (type !== 'service') {
        const label =
          options.find(({ selected }) => !!selected)?.label ?? undefined
        if (label) {
          acc.push(typeof label === 'object' ? i18n(label) : label)
        }
      }

      return acc
    }, [])
    .filter(Boolean)

  if (variants.length === 0) {
    return [props.product.titles.raw]
  }

  return variants
})

const selectedColorCode = computed(() => {
  return (
    funnel.value
      ?.find(({ id }) => id === 'color')
      ?.options.find(({ selected }) => !!selected)?.color ?? undefined
  )
})

const offers = computed(() => {
  const mobilePlanItems = props.pickersServices?.pickerGroups?.find(
    ({ id }) => id === 'mobile_plan',
  )?.items

  if (!mobilePlanItems) return []

  return mobilePlanItems
    .filter(hasItemExtraData)
    .map(({ mobilePlanOffer }) => mobilePlanOffer)
})

const showSimModal = computed(() => {
  return props.pickers.pickerGroups?.some(({ id }) => id === 'dual_sim')
})
const showMobilePlanOffersDrawer = computed(() => {
  return props.pickersServices?.pickerGroups?.some(
    ({ id }) => id === 'mobile_plan',
  )
})
const showProcessorModal = computed(() => {
  return props.pickers.pickerGroups?.some(
    ({ id }) =>
      id === 'processor_type_and_speed' ||
      id === 'processor_type_and_graphic_card' ||
      id === 'processor',
  )
})

const showMacbookProcessorModal = computed(() => {
  return (
    showProcessorModal.value &&
    MACBOOKS_CATEGORIES.includes(props.product.tracking.categoryId)
  )
})

const showLaptopProcessorModal = computed(() => {
  return (
    showProcessorModal.value &&
    ProductCategory.LAPTOP === props.product.tracking.categoryId
  )
})

watch(
  [() => props.pickersServices],
  async () => {
    const areMobilePlanAvailable = funnel.value
      .find(({ id }) => id === 'mobile_plan')
      ?.options.slice(1)
      .every((option) => !option.available)

    if (areMobilePlanAvailable && mobilePlan.value) {
      await navigateTo({
        path: route.path,
        query: {
          ...route.query,
          mobilePlanOfferId: undefined,
        },
        hash: route.hash,
        replace: true,
      })
    }
  },
  { immediate: true },
)
</script>
